import * as Sentry from "@sentry/react";
import { Form, Formik } from "formik";
import React, { useMemo, useState } from "react";

import { AssessmentDetail, Question, Questionnaire } from "../../globals/types";
import { useRefetchAssessment } from "../../hooks/useAssessmentDetail";
import useSectionIndex from "../../hooks/useSectionIndex";
import useToken from "../../hooks/useToken";
import { authHeader, formsFetch, unwrap } from "../../lib/formsApi";
import { track } from "../../lib/track";
import ErrorDialog from "../Questionnaire/ErrorDialog";
import Footer from "../Questionnaire/Footer";

import { answerListToMap, getInitialAnswerValues } from "./helpers";
import Section from "./Section";
import { useSubmittedDialogs } from "./SubmittedDialogs";
import { createValidationSchema } from "./yup-helpers";

type Props = {
    assessment: AssessmentDetail;
    questionnaire: Questionnaire;
    preview?: boolean;
    readOnly?: boolean;
    refetch: () => Promise<unknown>;
};

const AssessmentForm: React.FC<Props> = ({ assessment, questionnaire, preview, readOnly, refetch }) => {
    const [errorSubmitting, setErrorSubmitting] = useState(false);
    const [missingAnswers, setMissingAnswers] = useState<Question[]>([]);
    const [submitBlocked, setSubmitBlocked] = useState(false);

    const { show: showSubmitted, dialogs } = useSubmittedDialogs(assessment.type);
    const { token } = useToken();
    const refetchAssessment = useRefetchAssessment();

    const submit = async () => {
        try {
            const result = unwrap(
                await formsFetch.POST("/api/ast/{id}/submit", {
                    params: { path: { id: assessment.id } },
                    headers: authHeader(token),
                }),
                "submit assessment"
            );
            if (!result.ok) {
                setMissingAnswers((result.missingResponse ?? []) as Question[]);
                setErrorSubmitting(true);
                return;
            }
        } catch (err) {
            Sentry.captureException(err);
            setMissingAnswers([]);
            setErrorSubmitting(true);
            return;
        }
        await refetchAssessment().catch((err) => Sentry.captureException(err));
        showSubmitted();
    };

    const [sectionIndex] = useSectionIndex();
    const section = questionnaire.sections[sectionIndex];

    const answers = answerListToMap(assessment.answers);
    const initialValues = getInitialAnswerValues(questionnaire, answers);
    const validationSchema = useMemo(() => createValidationSchema(questionnaire.sections), [questionnaire.sections]);

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async () => {
                    if (preview || assessment.submittedAt) return;
                    track("submitted form", {
                        type: assessment.type,
                        sharingConsent: assessment.dataSharingConsent,
                    });
                    await submit();
                }}
            >
                <Form style={{ width: "100%" }}>
                    <Section
                        key={section.id}
                        section={section}
                        preview={preview}
                        answers={answers}
                        disabled={readOnly || !assessment.editable}
                        setSubmitBlocked={setSubmitBlocked}
                    />
                    {assessment.editable && (
                        <Footer
                            sectionCount={questionnaire.sections.length}
                            disabled={submitBlocked}
                            canSubmit={!readOnly && !assessment.submittedAt}
                            refetch={refetch}
                        />
                    )}

                    <ErrorDialog
                        errorSubmitting={errorSubmitting}
                        missingAnswers={missingAnswers}
                        closeModal={() => {
                            setErrorSubmitting(false);
                        }}
                        questionnaire={questionnaire}
                    />
                </Form>
            </Formik>
            {dialogs}
        </>
    );
};

export default AssessmentForm;
